// client/src/components/pages/CustomSummaries/SummaryCard.js
import React from 'react';
import {
  FaPlay,
  FaEdit,
  FaCopy,
  FaTrash,
  FaShare,
  FaClock,
  FaChartBar,
  FaTable,
  FaChartLine,
  FaChartPie,
  FaStar,
  FaRegStar,
  FaUser,
  FaGlobe,
  FaLock,
  FaEllipsisV,
} from 'react-icons/fa';
import { formatMiningDate } from '../../../utils/dateUtils';

const SummaryCard = ({
  summary,
  viewMode = 'grid',
  onExecute,
  onEdit,
  onDuplicate,
  onDelete,
  onShare,
  onToggleFavorite,
}) => {
  const getVisualizationIcon = (type) => {
    switch (type) {
      case 'chart':
        return <FaChartBar className="text-primary-500" />;
      case 'line':
        return <FaChartLine className="text-green-500" />;
      case 'pie':
        return <FaChartPie className="text-purple-500" />;
      default:
        return <FaTable className="text-gray-500" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return formatMiningDate(dateString);
  };

  const formatExecutionTime = (ms) => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const getCategoryColor = (category) => {
    const colors = {
      Operations: 'bg-blue-100 text-blue-800',
      Analytics: 'bg-green-100 text-green-800',
      Locations: 'bg-purple-100 text-purple-800',
      Financial: 'bg-yellow-100 text-yellow-800',
      Performance: 'bg-red-100 text-red-800',
      default: 'bg-gray-100 text-gray-800',
    };
    return colors[category] || colors.default;
  };

  if (viewMode === 'list') {
    return (
      <div className="bg-surface rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow duration-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 flex-1">
            {/* Icon and Basic Info */}
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gray-50 rounded-lg">
                {getVisualizationIcon(summary.VisualizationType)}
              </div>

              <div>
                <div className="flex items-center space-x-2">
                  <h3 className="text-lg font-semibold text-gray-900">{summary.Title}</h3>
                  <button
                    onClick={onToggleFavorite}
                    className="text-yellow-400 hover:text-yellow-500 transition-colors duration-200"
                  >
                    {summary.IsFavorite ? <FaStar /> : <FaRegStar />}
                  </button>
                  {summary.IsPublic ? (
                    <FaGlobe className="text-green-500" title="Public" />
                  ) : (
                    <FaLock className="text-gray-400" title="Private" />
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-1">{summary.Description}</p>
              </div>
            </div>

            {/* Category and Tags */}
            <div className="flex items-center space-x-2">
              {summary.Category && (
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(summary.Category)}`}
                >
                  {summary.Category}
                </span>
              )}
              {summary.Tags &&
                summary.Tags.slice(0, 2).map((tag, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs"
                  >
                    {tag}
                  </span>
                ))}
              {summary.Tags && summary.Tags.length > 2 && (
                <span className="text-xs text-gray-500">+{summary.Tags.length - 2} more</span>
              )}
            </div>

            {/* Stats */}
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <div className="flex items-center space-x-1">
                <FaPlay className="text-xs" />
                <span>{summary.ExecutionCount || 0} runs</span>
              </div>
              <div className="flex items-center space-x-1">
                <FaClock className="text-xs" />
                <span>{formatExecutionTime(summary.AverageExecutionTime)}</span>
              </div>
              <div className="flex items-center space-x-1">
                <FaUser className="text-xs" />
                <span>{summary.CreatedBy}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2">
            <button
              onClick={onExecute}
              className="flex items-center px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-md text-sm transition-colors duration-200"
            >
              <FaPlay className="mr-1" />
              Execute
            </button>

            <div className="relative group">
              <button className="p-2 text-gray-400 hover:text-gray-600 rounded-md">
                <FaEllipsisV />
              </button>

              <div className="absolute right-0 top-full mt-1 w-48 bg-surface rounded-md shadow-lg border border-gray-200 py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                <button
                  onClick={onEdit}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <FaEdit className="mr-2" />
                  Edit
                </button>
                <button
                  onClick={onDuplicate}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <FaCopy className="mr-2" />
                  Duplicate
                </button>
                <button
                  onClick={onShare}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <FaShare className="mr-2" />
                  Share
                </button>
                <hr className="my-1" />
                <button
                  onClick={onDelete}
                  className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <FaTrash className="mr-2" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Grid view
  return (
    <div className="bg-surface rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
      {/* Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-gray-50 rounded-lg flex-shrink-0">
            {getVisualizationIcon(summary.VisualizationType)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start space-x-2 mb-1">
              <h3 className="text-lg font-semibold text-gray-900 flex-1 break-words">
                {summary.Title}
              </h3>
              <button
                onClick={onToggleFavorite}
                className="text-yellow-400 hover:text-yellow-500 transition-colors duration-200 flex-shrink-0 mt-0.5"
              >
                {summary.IsFavorite ? <FaStar /> : <FaRegStar />}
              </button>
            </div>
            <div className="flex items-center space-x-2">
              {summary.IsPublic ? (
                <FaGlobe className="text-green-500 text-sm" title="Public" />
              ) : (
                <FaLock className="text-gray-400 text-sm" title="Private" />
              )}
              <span className="text-sm text-gray-500">{summary.UserPermission}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{summary.Description}</p>

        {/* Category and Tags */}
        <div className="mb-3">
          {summary.Category && (
            <span
              className={`inline-block px-2 py-1 rounded-full text-xs font-medium mb-2 ${getCategoryColor(summary.Category)}`}
            >
              {summary.Category}
            </span>
          )}
          {summary.Tags && summary.Tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {summary.Tags.slice(0, 3).map((tag, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs"
                >
                  {tag}
                </span>
              ))}
              {summary.Tags.length > 3 && (
                <span className="text-xs text-gray-500 px-2 py-1">+{summary.Tags.length - 3}</span>
              )}
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-2 text-xs text-gray-500 mb-4">
          <div className="flex items-center space-x-1">
            <FaPlay />
            <span>{summary.ExecutionCount || 0} runs</span>
          </div>
          <div className="flex items-center space-x-1">
            <FaClock />
            <span>{formatExecutionTime(summary.AverageExecutionTime)}</span>
          </div>
          <div className="flex items-center space-x-1 col-span-2">
            <FaUser />
            <span>by {summary.CreatedBy}</span>
          </div>
        </div>

        {/* Dates */}
        <div className="text-xs text-gray-400 mb-4">
          <div>Created: {formatDate(summary.CreatedAt)}</div>
          <div>Last run: {formatDate(summary.LastExecuted)}</div>
        </div>
      </div>

      {/* Actions */}
      <div className="px-4 pb-4">
        <div className="flex items-center justify-between">
          <button
            onClick={onExecute}
            className="flex items-center px-3 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md text-sm transition-colors duration-200"
          >
            <FaPlay className="mr-2" />
            Execute
          </button>

          <div className="flex items-center space-x-1">
            <button
              onClick={onEdit}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-md transition-colors duration-200"
              title="Edit"
            >
              <FaEdit />
            </button>
            <button
              onClick={onDuplicate}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-md transition-colors duration-200"
              title="Duplicate"
            >
              <FaCopy />
            </button>
            <button
              onClick={onShare}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-md transition-colors duration-200"
              title="Share"
            >
              <FaShare />
            </button>
            <button
              onClick={onDelete}
              className="p-2 text-red-400 hover:text-red-600 rounded-md transition-colors duration-200"
              title="Delete"
            >
              <FaTrash />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SummaryCard;
